// Rewards entry fee gate (rewards part 3, migration 401) — "Complete your
// membership". A seller org that owes the one-time entry fee sees this
// non-dismissible screen before its panel: the fee, the credit it gets right
// away and the free trial, then one card form that pays and saves the card.
// Paid is decided by the Stripe webhook; the screen waits for it.
// Spec: docs/superpowers/specs/2026-11-03-rewards-program-design.md → "Entry fee flow"
//
// Shown after the personal gates and the org Terms gate (PostLoginGates).

import { useState } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { loadStripe, type Stripe, type StripeElementsOptions } from '@stripe/stripe-js'
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import {
  EntryFeeApiError,
  entryFeeKey,
  fetchEntryFee,
  startEntryFeePayment,
  type EntryFeePayment,
  type EntryFeeStatus,
} from '../../api/entryFee'
import { Button } from '../../components/ui/button'
import { DialogOverlay, DialogTitle } from '../../components/ui/dialog'
import { DialogPortal } from '../../components/ui/dialog.lib'
import { useT } from '../../i18n'
import { entryFeeGateI18n, type EntryFeeGateStrings } from '../../i18n/onboarding/entry-fee'
import { termsAcceptanceI18n } from '../../i18n/onboarding/terms-acceptance'
import { formatCurrency } from '../../lib/currency'
import { useAuthStore } from '../../store/auth'
import { useGateEnabled } from './useGateEnabled'
import { useRequiredAgreements } from './useRequiredAgreements'

const SUPPORT_EMAIL = 'support@pexify.net'

function usd(value: string | number | null): string {
  return formatCurrency(Number(value ?? 0), 'USD')
}

export function EntryFeeGate() {
  const navigate = useNavigate()
  const enabled = useGateEnabled()
  // The org Terms gate comes first; this one waits until it is clear.
  const terms = useRequiredAgreements(enabled)
  return (
    <EntryFeeGateModal
      enabled={enabled && terms.status === 'none'}
      onLogout={() => navigate({ to: '/login' })}
    />
  )
}

export function EntryFeeGateModal({
  enabled,
  onLogout,
}: {
  enabled: boolean
  onLogout?: () => void
}) {
  const t = useT(entryFeeGateI18n)
  const [paid, setPaid] = useState(false)
  const query = useQuery({
    queryKey: entryFeeKey,
    queryFn: fetchEntryFee,
    enabled,
    retry: false,
    // While Stripe's confirmation is on its way, look again every few seconds.
    refetchInterval: (q) =>
      paid || (q.state.data as EntryFeeStatus | undefined)?.processing ? 3000 : false,
  })
  const clearTokens = useAuthStore((s) => s.clearTokens)

  const data = query.data
  if (!enabled || !data || data.state !== 'due') return null

  const logout = () => {
    clearTokens()
    onLogout?.()
  }
  const waiting = paid || data.processing

  return (
    <DialogPrimitive.Root open>
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          // Non-dismissible: no Escape, no outside click, no close X.
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          aria-describedby={undefined}
          className="fixed left-1/2 top-1/2 z-50 grid w-full max-w-[calc(100vw-1.5rem)] -translate-x-1/2 -translate-y-1/2 gap-4 overflow-y-auto rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-5 text-[var(--color-text)] shadow-xl max-h-[calc(100dvh-1.5rem)] sm:max-h-[92vh] sm:max-w-lg sm:p-6"
        >
          <DialogTitle>{waiting ? t.processingTitle : t.title}</DialogTitle>
          {waiting ? (
            <p role="status" className="text-sm text-[var(--color-text-muted)]">
              {t.processingBody}
            </p>
          ) : (
            <>
              <p className="text-sm text-[var(--color-text-muted)]">{t.intro}</p>
              <Summary data={data} t={t} />
              {data.failure_reason && (
                <p role="alert" className="text-sm text-[var(--color-danger)]">
                  {t.failed} {data.failure_reason}
                </p>
              )}
              <PayStep amount={usd(data.amount_usd)} onPaid={() => setPaid(true)} t={t} />
            </>
          )}
          <GateFooter onLogout={logout} />
        </DialogPrimitive.Content>
      </DialogPortal>
    </DialogPrimitive.Root>
  )
}

function Summary({ data, t }: { data: EntryFeeStatus; t: EntryFeeGateStrings }) {
  return (
    <div className="space-y-3">
      <dl className="divide-y divide-[var(--color-border)] rounded-[var(--radius-panel)] border border-[var(--color-border)] text-sm">
        <div className="flex items-center justify-between gap-3 px-3 py-2">
          <dt>{t.rows.fee}</dt>
          <dd className="font-semibold tabular-nums">{usd(data.amount_usd)}</dd>
        </div>
        <div className="flex items-start justify-between gap-3 px-3 py-2">
          <dt>
            {t.rows.credit}
            <span className="block text-xs text-[var(--color-text-muted)]">{t.rows.creditHint}</span>
          </dt>
          <dd className="font-semibold tabular-nums text-[var(--color-success)]">
            +{usd(data.credit_usd)}
          </dd>
        </div>
        <div className="flex items-center justify-between gap-3 px-3 py-2">
          <dt>{t.rows.trial}</dt>
          <dd className="font-semibold tabular-nums">{t.rows.trialValue(data.trial_days ?? 0)}</dd>
        </div>
      </dl>
      {t.covers.length > 0 && (
        <div>
          <div className="text-xs font-medium text-[var(--color-text-muted)]">{t.coversTitle}</div>
          <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm">
            {t.covers.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

function PayStep({
  amount,
  onPaid,
  t,
}: {
  amount: string
  onPaid: () => void
  t: EntryFeeGateStrings
}) {
  const [intent, setIntent] = useState<EntryFeePayment | null>(null)
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null)
  const [starting, setStarting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function start() {
    setStarting(true)
    setError(null)
    try {
      const data = await startEntryFeePayment()
      if (!data.publishable_key) {
        setError(t.stripeMissing)
        return
      }
      setStripePromise(loadStripe(data.publishable_key))
      setIntent(data)
    } catch (e) {
      // Say it in the seller's words; never echo a gateway's internal message.
      setError(
        e instanceof EntryFeeApiError && e.code === 'payment_gateway_unconfigured'
          ? t.stripeMissing
          : `${t.failed} ${t.tryAgain}`,
      )
    } finally {
      setStarting(false)
    }
  }

  if (!intent || !stripePromise) {
    return (
      <div className="space-y-2">
        {error && (
          <p role="alert" className="text-sm text-[var(--color-danger)]">
            {error}
          </p>
        )}
        <Button variant="primary" className="w-full" disabled={starting} onClick={() => void start()}>
          {starting ? t.starting : t.start}
        </Button>
      </div>
    )
  }

  const options: StripeElementsOptions = {
    clientSecret: intent.client_secret,
    ...(intent.customer_session_client_secret
      ? { customerSessionClientSecret: intent.customer_session_client_secret }
      : {}),
    appearance: { theme: 'stripe' },
  }
  return (
    <Elements stripe={stripePromise} options={options}>
      <CardForm amount={amount} onPaid={onPaid} t={t} />
    </Elements>
  )
}

function CardForm({
  amount,
  onPaid,
  t,
}: {
  amount: string
  onPaid: () => void
  t: EntryFeeGateStrings
}) {
  const stripe = useStripe()
  const elements = useElements()
  const queryClient = useQueryClient()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function pay() {
    if (!stripe || !elements) return
    setSubmitting(true)
    setError(null)
    const { error: payError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    })
    if (payError) {
      setError(`${t.failed} ${payError.message ?? t.tryAgain}`)
      setSubmitting(false)
      return
    }
    if (paymentIntent && (paymentIntent.status === 'succeeded' || paymentIntent.status === 'processing')) {
      onPaid()
      await queryClient.invalidateQueries({ queryKey: entryFeeKey })
      return
    }
    setSubmitting(false)
  }

  return (
    <div className="space-y-3">
      <PaymentElement />
      {error && (
        <p role="alert" className="text-sm text-[var(--color-danger)]">
          {error}
        </p>
      )}
      <Button variant="primary" className="w-full" disabled={!stripe || submitting} onClick={() => void pay()}>
        {submitting ? t.paying : t.pay(amount)}
      </Button>
    </div>
  )
}

function GateFooter({ onLogout }: { onLogout: () => void }) {
  const t = useT(termsAcceptanceI18n).footer
  return (
    <div className="flex flex-col gap-2 border-t border-[var(--color-border)] pt-3 text-xs text-[var(--color-text-muted)] sm:flex-row sm:items-center sm:justify-between">
      <span>
        {t.needHelp}{' '}
        <a className="underline" href={`mailto:${SUPPORT_EMAIL}`}>
          {t.contactSupport}
        </a>
      </span>
      <button type="button" onClick={onLogout} className="text-left underline sm:text-right">
        {t.logOut}
      </button>
    </div>
  )
}
